/* ==========================================================================
   Nash Market — comportements de la boutique
   Principe directeur : aucune opération ne doit jamais détruire le panier.
   Chaque ligne est modifiée indépendamment ; une erreur sur un article
   laisse tous les autres intacts.
   ========================================================================== */
(() => {
  'use strict';

  const cfg = window.Nash || {};
  const ROOT = cfg.root || '/';
  const S = cfg.strings || {};
  const LOW = Number(cfg.lowStock || 5);

  const $  = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* ---------- Format monétaire : reprend celui de la boutique --------------- */
  function money(cents) {
    const amount = (Number(cents) / 100);
    const fmt = cfg.moneyFormat || '{{amount_with_comma_separator}} €';
    const withComma = amount.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const withDot   = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(amount).toString())
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(amount).toString())
      .replace(/\{\{\s*amount\s*\}\}/g, withDot)
      .replace(/<[^>]*>/g, '')
      .trim();
  }

  /* ---------- Notifications ------------------------------------------------ */
  const toastEl = $('[data-toast]');
  let toastTimer;
  function toast(message, kind = '') {
    if (!toastEl || !message) return;
    toastEl.textContent = message;
    toastEl.className = 'toast is-visible' + (kind ? ' toast--' + kind : '');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { toastEl.className = 'toast'; }, kind === 'error' ? 5200 : 3200);
  }

  /* ---------- Panneaux coulissants avec piège de focus --------------------- */
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';
  let openPanel = null;
  let lastFocused = null;

  function openDrawer(panel) {
    if (!panel) return;
    if (openPanel && openPanel !== panel) closeDrawer(openPanel);
    lastFocused = document.activeElement;
    panel.hidden = false;
    const backdrop = $('[data-backdrop]');
    if (backdrop) { backdrop.hidden = false; requestAnimationFrame(() => backdrop.classList.add('is-open')); }
    requestAnimationFrame(() => panel.classList.add('is-open'));
    document.body.classList.add('is-locked');
    $$(`[aria-controls="${panel.id}"]`).forEach(b => b.setAttribute('aria-expanded', 'true'));
    openPanel = panel;
    setTimeout(() => (panel.querySelector('[data-autofocus]') || panel.querySelector(FOCUSABLE))?.focus(), 60);
  }

  function closeDrawer(panel = openPanel) {
    if (!panel) return;
    panel.classList.remove('is-open');
    const backdrop = $('[data-backdrop]');
    if (backdrop) backdrop.classList.remove('is-open');
    document.body.classList.remove('is-locked');
    $$(`[aria-controls="${panel.id}"]`).forEach(b => b.setAttribute('aria-expanded', 'false'));
    setTimeout(() => {
      panel.hidden = true;
      if (backdrop) backdrop.hidden = true;
    }, 250);
    if (openPanel === panel) openPanel = null;
    lastFocused?.focus?.();
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      const panel = document.getElementById(opener.getAttribute('aria-controls'));
      panel && (panel.classList.contains('is-open') ? closeDrawer(panel) : openDrawer(panel));
      return;
    }
    if (e.target.closest('[data-drawer-close]') || e.target.closest('[data-backdrop]')) {
      e.preventDefault();
      closeDrawer();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && openPanel) { closeDrawer(); return; }
    if (e.key === 'Tab' && openPanel) {
      const items = $$(FOCUSABLE, openPanel).filter(el => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---------- Suggestions de recherche ------------------------------------- */
  $$('[data-predictive]').forEach(setupPredictive);

  function setupPredictive(wrap) {
    const input   = $('input[type="search"]', wrap);
    const results = $('[data-predictive-results]', wrap);
    const bar     = wrap.closest('.searchbar') || wrap;
    if (!input || !results) return;

    let timer, controller, items = [], cursor = -1;

    const close = () => {
      results.hidden = true; results.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      cursor = -1; items = [];
    };

    const render = (products, term) => {
      if (!products.length) {
        results.innerHTML = `<div class="predictive__empty">${esc(S.noResults || '')}</div>`;
      } else {
        results.innerHTML = products.map(p => {
          const img = p.featured_image?.url || p.image || '';
          return `<a class="predictive__item" href="${esc(p.url)}">
            <span class="predictive__img">${img ? `<img src="${esc(img)}" alt="" width="46" height="46" loading="lazy">` : ''}</span>
            <span class="predictive__text"><strong>${esc(p.title)}</strong>${p.vendor ? `<small>${esc(p.vendor)}</small>` : ''}</span>
            <span class="predictive__price">${esc(money(p.price))}</span>
          </a>`;
        }).join('') +
        `<a class="predictive__all" href="${ROOT}search?q=${encodeURIComponent(term)}&type=product">${esc(S.viewAll || '')}</a>`;
      }
      items = $$('a', results);
      cursor = -1;
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    on(input, 'input', () => {
      bar.classList.toggle('has-value', input.value.length > 0);
      clearTimeout(timer);
      const term = input.value.trim();
      if (term.length < 2) return close();
      timer = setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        const url = `${ROOT}search/suggest.json?q=${encodeURIComponent(term)}`
          + `&resources[type]=product&resources[limit]=8`
          + `&resources[options][unavailable_products]=last`
          + `&resources[options][fields]=title,vendor,product_type,variants.title,variants.sku,variants.barcode,tag`;
        try {
          const res = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
          if (!res.ok) throw new Error('search');
          const data = await res.json();
          render(data.resources?.results?.products || [], term);
        } catch (err) { if (err.name !== 'AbortError') close(); }
      }, 180);
    });

    on(input, 'keydown', (e) => {
      if (results.hidden || !items.length) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        cursor = e.key === 'ArrowDown'
          ? (cursor + 1) % items.length
          : (cursor - 1 + items.length) % items.length;
        items.forEach((a, i) => a.classList.toggle('is-active', i === cursor));
        items[cursor].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && cursor > -1) {
        e.preventDefault(); items[cursor].click();
      }
    });

    const clear = $('[data-search-clear]', wrap);
    on(clear, 'click', () => { input.value = ''; bar.classList.remove('has-value'); close(); input.focus(); });
    document.addEventListener('click', (e) => { if (!wrap.contains(e.target)) close(); });
  }

  /* ---------- Sélecteurs de quantité (hors panier) ------------------------- */
  function clampInput(input) {
    const min = Number(input.min || 1);
    const max = input.max ? Number(input.max) : Infinity;
    let v = Math.floor(Number(input.value) || min);
    if (!Number.isFinite(v)) v = min;
    const clamped = Math.min(max, Math.max(min, v));
    const wasClamped = clamped !== v;
    input.value = String(clamped);
    return { value: clamped, clamped: wasClamped, atMax: clamped >= max && max !== Infinity };
  }

  function bindQty(scope = document) {
    $$('[data-qty]', scope).forEach(box => {
      if (box.dataset.bound) return;
      box.dataset.bound = '1';
      const input = $('input', box);
      const minus = $('[data-qty-minus]', box);
      const plus  = $('[data-qty-plus]', box);
      if (!input) return;

      /* Le « − » est désactivé au minimum : la raison est évidente.
         Le « + » reste actif au maximum et affiche un message : un bouton
         grisé n'explique pas au client pourquoi il ne peut pas continuer. */
      const sync = () => {
        const v = Number(input.value) || 1;
        if (minus) minus.disabled = v <= Number(input.min || 1);
      };

      on(minus, 'click', () => { input.value = Number(input.value || 1) - 1; clampInput(input); sync(); });
      on(plus,  'click', () => {
        const max = input.max ? Number(input.max) : Infinity;
        const next = Number(input.value || 1) + 1;
        if (next > max) { toast(S.maxReached, 'warn'); sync(); return; }
        input.value = next; clampInput(input); sync();
      });
      on(input, 'change', () => { if (clampInput(input).clamped) toast(S.maxReached, 'warn'); sync(); });
      sync();
    });
  }
  bindQty();

  /* ---------- Rendu de sections (API Shopify) ------------------------------ */
  const SECTION_TARGETS = [
    { id: 'cart-drawer', selector: '[data-cart-drawer-contents]' },
    { id: 'main-cart',   selector: '[data-cart-main]' },
  ];
  const sectionIds = () => SECTION_TARGETS.filter(t => $(t.selector)).map(t => t.id).join(',');

  function applySections(sections) {
    if (!sections) return;
    SECTION_TARGETS.forEach(({ id, selector }) => {
      const html = sections[id];
      const target = $(selector);
      if (!html || !target) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      if (fresh) { target.innerHTML = fresh.innerHTML; }
    });
    bindQty();
    bindCartLines();
    bindCheckoutForms();
    updateBubbles();
  }

  function updateBubbles(count) {
    if (typeof count === 'number') {
      $$('[data-cart-count]').forEach(el => { el.textContent = count; el.dataset.count = count; });
    }
    $$('[data-cart-count]').forEach(el => {
      el.classList.remove('cart-bump');
      void el.offsetWidth;
      el.classList.add('cart-bump');
    });
  }

  /* ---------- Animation vers le panier ------------------------------------- */
  function flyToCart(sourceImg) {
    if (!sourceImg || reduceMotion()) return;
    const target = $('[data-cart-anchor]');
    if (!target) return;
    const from = sourceImg.getBoundingClientRect();
    const to   = target.getBoundingClientRect();
    if (!from.width) return;
    const size = Math.min(from.width, 96);
    const el = document.createElement('div');
    el.className = 'fly-to-cart';
    el.style.cssText = `left:${from.left}px;top:${from.top}px;width:${size}px;height:${size}px;`;
    el.innerHTML = `<img src="${esc(sourceImg.currentSrc || sourceImg.src)}" alt="">`;
    document.body.appendChild(el);
    requestAnimationFrame(() => {
      el.style.transform = `translate(${to.left + to.width / 2 - from.left - size / 2}px, ${to.top + to.height / 2 - from.top - size / 2}px) scale(.15)`;
      el.style.opacity = '0.25';
    });
    setTimeout(() => el.remove(), 700);
  }

  /* ---------- Ajout au panier ---------------------------------------------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-add-form]');
    if (!form) return;
    e.preventDefault();

    const button = $('button[type="submit"]', form);
    if (button?.classList.contains('is-loading')) return;
    button?.classList.add('is-loading');
    button && (button.disabled = true);

    const data = new FormData(form);
    const variantId = String(data.get('id') || '');
    const wanted = Math.max(1, Number(data.get('quantity') || 1));

    try {
      const body = { items: [{ id: variantId, quantity: wanted }] };
      const ids = sectionIds();
      if (ids) body.sections = ids;

      let res = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.status === 422) {
        /* Stock insuffisant. Shopify n'a rien ajouté et n'a rien retiré du panier.
           On calcule ce qui reste réellement disponible et on ajoute cette quantité. */
        const cart = await (await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })).json();
        const already = cart.items
          .filter(i => String(i.variant_id) === variantId)
          .reduce((n, i) => n + i.quantity, 0);
        const declaredMax = Number(form.dataset.inventoryMax || 0);
        const remaining = declaredMax > 0 ? Math.max(0, declaredMax - already) : 0;

        if (remaining < 1) {
          toast(S.maxReached, 'warn');
          updateBubbles(cart.item_count);
          return;
        }
        body.items[0].quantity = remaining;
        res = await fetch(`${ROOT}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        if (res.ok) {
          const payload = await res.json();
          applySections(payload.sections);
          const c = await (await fetch(`${ROOT}cart.js`)).json();
          updateBubbles(c.item_count);
          toast(fmtAdjusted(remaining), 'warn');
          return;
        }
      }

      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.description || payload.message || S.addError);
      }

      const payload = await res.json();
      applySections(payload.sections);
      const cart = await (await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })).json();
      updateBubbles(cart.item_count);

      flyToCart(form.closest('.card, .nm-cell')?.querySelector('.card__media img, .nm-cell__lien-img img') || $('#ProductMainImage'));
      toast(S.added);

      if (cfg.cartType === 'drawer' && form.dataset.openDrawer === 'true') {
        openDrawer($('#CartDrawer'));
      }
    } catch (err) {
      toast(err.message || S.addError, 'error');
    } finally {
      button?.classList.remove('is-loading');
      button && (button.disabled = false);
    }
  });

  function fmtAdjusted(n) {
    return (S.adjusted || '').replace('{{ count }}', n).replace('{{count}}', n);
  }

  /* ---------- Modification d'une ligne de panier --------------------------- *
     Une seule ligne est envoyée à la fois. Shopify plafonne automatiquement la
     quantité au stock disponible et renvoie le panier corrigé : aucune autre
     ligne n'est touchée, même si l'article demandé est devenu indisponible.   */
  /** Relit, côté serveur, la quantité maximale encore disponible pour une ligne.
   *  Le rendu de section renvoie du Liquid frais, donc un stock à jour. */
  async function liveMaxFor(key) {
    try {
      const res = await fetch(`${ROOT}cart?sections=main-cart,cart-drawer`,
                              { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const data = await res.json();
      const html = data['main-cart'] || data['cart-drawer'];
      if (!html) return null;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const row = doc.querySelector(`[data-cart-line][data-key="${CSS.escape(key)}"]`);
      const max = row?.querySelector('[data-cart-qty]')?.getAttribute('max');
      return max === null || max === undefined ? null : Number(max);
    } catch (_) {
      return null;
    }
  }

  async function changeLine(key, quantity, lineEl) {
    const row = lineEl || $(`[data-cart-line][data-key="${CSS.escape(key)}"]`);
    row?.classList.add('is-busy');
    const previous = Number(row?.dataset.quantity || 0);
    const requested = Math.max(0, Math.floor(Number(quantity) || 0));

    try {
      const body = { id: key, quantity: requested };
      const ids = sectionIds();
      if (ids) body.sections = ids;

      const res = await fetch(`${ROOT}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        /* Selon les cas, Shopify plafonne la quantité (200) ou refuse (422).
           Sur refus, on relit l'état réel du panier et on réessaie avec le
           maximum réellement disponible, plutôt que d'abandonner la ligne. */
        const payload = await res.json().catch(() => ({}));
        const cart = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })
          .then(r => (r.ok ? r.json() : null)).catch(() => null);
        const line = cart?.items?.find(i => i.key === key);

        /* Le maximum affiché date du rendu de la page. Si le stock a baissé
           depuis, il est périmé : on redemande au serveur l'état réel avant
           de conclure. */
        let declaredMax = row ? Number($('[data-cart-qty]', row)?.max || 0) : 0;
        const freshMax = await liveMaxFor(key);
        if (freshMax !== null) declaredMax = freshMax;

        if (requested > 0 && declaredMax > 0 && declaredMax < requested) {
          const retry = await fetch(`${ROOT}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ ...body, quantity: declaredMax }),
          });
          if (retry.ok) {
            const fixed = await retry.json();
            applySections(fixed.sections);
            updateBubbles(fixed.item_count);
            toast(fmtAdjusted(declaredMax), 'warn');
            return declaredMax;
          }
        }
        /* Échec réel : seule cette ligne revient à son état précédent.
           Toutes les autres lignes sont restées intactes côté serveur. */
        if (row) {
          const input = $('[data-cart-qty]', row);
          if (input) input.value = line ? line.quantity : previous;
        }
        if (cart) updateBubbles(cart.item_count);
        toast(payload.description || S.lineError, 'error');
        return null;
      }

      const cart = await res.json();
      const line = cart.items.find(i => i.key === key);
      const applied = line ? line.quantity : 0;

      applySections(cart.sections);
      updateBubbles(cart.item_count);

      if (requested > 0 && applied < requested) {
        toast(fmtAdjusted(applied), 'warn');
      }
      return applied;
    } catch (err) {
      /* Échec réseau ou serveur : on remet uniquement cette ligne dans son état
         précédent. Le reste du panier n'a jamais quitté le serveur. */
      if (row) {
        const input = $('[data-cart-qty]', row);
        if (input) input.value = previous;
        row.classList.remove('is-busy');
      }
      toast(S.lineError, 'error');
      return null;
    } finally {
      row?.classList.remove('is-busy');
    }
  }

  /* Le formulaire des lignes n'existe que comme repli sans JavaScript. Avec
     JavaScript, chaque ligne est modifiée seule : on empêche donc toute
     soumission en bloc, y compris celle déclenchée par la touche Entrée dans
     un champ de quantité. C'est précisément ce type d'envoi global qui pouvait
     bousculer l'ensemble du panier. */
  function bindCartLinesForm(scope = document) {
    $$('[data-cart-lines-form]', scope).forEach(form => {
      if (form.dataset.guarded) return;
      form.dataset.guarded = '1';
      on(form, 'submit', (e) => e.preventDefault());
    });
  }

  function bindCartLines(scope = document) {
    bindCartLinesForm(scope);
    $$('[data-cart-line]', scope).forEach(row => {
      if (row.dataset.bound) return;
      row.dataset.bound = '1';
      const key   = row.dataset.key;
      const input = $('[data-cart-qty]', row);
      const minus = $('[data-cart-minus]', row);
      const plus  = $('[data-cart-plus]', row);
      const max   = input?.max ? Number(input.max) : Infinity;

      on(minus, 'click', () => changeLine(key, Number(input.value || 0) - 1, row));
      on(plus,  'click', () => {
        const next = Number(input.value || 0) + 1;
        if (next > max) { toast(S.maxReached, 'warn'); return; }
        changeLine(key, next, row);
      });
      on(input, 'change', () => {
        let v = Math.max(0, Math.floor(Number(input.value) || 0));
        if (v > max) { v = max; input.value = v; toast(S.maxReached, 'warn'); }
        changeLine(key, v, row);
      });
      $$('[data-cart-remove]', row).forEach(btn => on(btn, 'click', (e) => {
        e.preventDefault(); changeLine(key, 0, row);
      }));
    });
  }
  bindCartLines();

  /* ---------- Contrôle avant paiement -------------------------------------- *
     Si un stock a baissé depuis la mise au panier, on ajuste la ou les lignes
     concernées et on prévient le client. On ne vide jamais le panier.         */
  function bindCheckoutForms(scope = document) {
    $$('[data-checkout-form]', scope).forEach(form => {
      if (form.dataset.bound) return;
      form.dataset.bound = '1';
      on(form, 'submit', onCheckoutSubmit);
    });
  }

  async function onCheckoutSubmit(e) {
    /* `currentTarget` est capturé avant tout await : il devient nul ensuite. */
    const form = e.currentTarget || e.target?.closest?.('[data-checkout-form]');
    if (!form || form.dataset.verified === '1') return;
    e.preventDefault();
    const btn = $('[name="checkout"]', form);
    btn?.classList.add('is-loading');

    try {
      const [cart, fresh] = await Promise.all([
        fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then(r => r.json()),
        fetch(`${ROOT}cart?sections=main-cart`, { headers: { Accept: 'application/json' } })
          .then(r => r.ok ? r.json() : null).catch(() => null),
      ]);

      const limits = new Map();
      if (fresh && fresh['main-cart']) {
        const doc = new DOMParser().parseFromString(fresh['main-cart'], 'text/html');
        doc.querySelectorAll('[data-cart-line]').forEach(row => {
          const m = row.querySelector('[data-cart-qty]')?.getAttribute('max');
          if (m) limits.set(row.dataset.key, Number(m));
        });
      }

      const excess = cart.items.filter(i => limits.has(i.key) && i.quantity > limits.get(i.key));
      if (excess.length) {
        for (const item of excess) {
          await changeLine(item.key, limits.get(item.key));
        }
        toast(fmtAdjusted(excess.map(i => limits.get(i.key)).join(', ')), 'warn');
        btn?.classList.remove('is-loading');
        return;   /* Le client voit son panier corrigé et valide lui-même. */
      }
    } catch (_) { /* En cas de doute on laisse Shopify décider au paiement. */ }

    form.dataset.verified = '1';
    form.requestSubmit ? form.requestSubmit(btn) : form.submit();
  }
  bindCheckoutForms();

  /* ---------- Page produit : variantes ------------------------------------- */
  $$('[data-variant-select]').forEach(select => on(select, 'change', () => {
    const opt = select.selectedOptions[0];
    if (!opt) return;
    const scope = select.closest('[data-product]');
    const form  = select.closest('[data-add-form]');
    const price = $('[data-price-current]', scope);
    const compare = $('[data-price-compare]', scope);
    const qty   = $('input[name="quantity"]', form);
    const panel = $('[data-stock-panel]', scope);
    const btn   = $('[data-add-button]', scope);

    const available = opt.dataset.available === 'true';
    const max = Math.max(0, Number(opt.dataset.max || 0));
    const tracked = opt.dataset.tracked === 'true';

    if (price && opt.dataset.price) price.textContent = opt.dataset.price;
    if (compare) {
      compare.textContent = opt.dataset.compare || '';
      compare.hidden = !opt.dataset.compare;
    }
    if (form) form.dataset.inventoryMax = String(max);
    if (qty) {
      if (tracked) qty.max = String(max); else qty.removeAttribute('max');
      qty.value = String(Math.max(1, Math.min(Number(qty.value || 1), tracked ? Math.max(1, max) : 99)));
      qty.closest('[data-qty]')?.removeAttribute('data-bound');
      bindQty(scope);
    }
    if (panel) {
      const label = $('[data-stock-label]', panel);
      panel.classList.toggle('stock-panel--out', !available);
      panel.classList.toggle('stock-panel--low', available && tracked && max > 0 && max <= LOW);
      if (label) {
        if (!available) label.textContent = S.soldOut;
        else if (tracked && max > 0 && max <= LOW) {
          label.textContent = (max === 1 ? S.lowStockOne : S.lowStockMany).replace(/\{\{\s*count\s*\}\}/g, max);
        } else label.textContent = S.inStock;
      }
    }
    if (btn) {
      btn.disabled = !available;
      btn.textContent = available ? (btn.dataset.labelAdd || btn.textContent) : S.soldOut;
    }
    if (opt.dataset.image) {
      const main = $('#ProductMainImage');
      if (main) { main.src = opt.dataset.image; main.removeAttribute('srcset'); }
    }
    if (opt.dataset.url && window.history.replaceState) {
      window.history.replaceState({}, '', opt.dataset.url);
    }
  }));

  /* ---------- Galerie produit ---------------------------------------------- */
  $$('[data-gallery]').forEach(gallery => {
    const main = $('[data-gallery-main]', gallery);
    $$('[data-gallery-thumb]', gallery).forEach(thumb => on(thumb, 'click', () => {
      if (!main) return;
      main.src = thumb.dataset.full;
      main.removeAttribute('srcset');
      main.alt = thumb.dataset.alt || '';
      $$('[data-gallery-thumb]', gallery).forEach(t => {
        t.classList.toggle('is-active', t === thumb);
        t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
      });
    }));
  });

  const zoomDialog = $('[data-zoom-dialog]');
  $$('[data-zoom-open]').forEach(btn => on(btn, 'click', () => {
    const src = $('[data-gallery-main]');
    const img = $('img', zoomDialog);
    if (!src || !img || typeof zoomDialog.showModal !== 'function') return;
    img.src = src.currentSrc || src.src;
    img.alt = src.alt;
    // Reprendre le format réel de la photo : le cadre du zoom est ainsi
    // réservé avant le chargement, sans saut de mise en page.
    if (src.naturalWidth && src.naturalHeight) {
      img.width = src.naturalWidth;
      img.height = src.naturalHeight;
    }
    zoomDialog.showModal();
  }));
  on($('[data-zoom-close]'), 'click', () => zoomDialog?.close());
  on(zoomDialog, 'click', (e) => { if (e.target === zoomDialog) zoomDialog.close(); });

  /* ---------- Filtres de collection (mobile) ------------------------------- */
  $$('[data-facets-toggle]').forEach(btn => on(btn, 'click', () => {
    const panel = $('[data-facets]');
    if (!panel) return;
    const open = panel.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', String(open));
    document.body.classList.toggle('is-locked', open);
  }));
  $$('[data-facets-close]').forEach(btn => on(btn, 'click', () => {
    $('[data-facets]')?.classList.remove('is-open');
    document.body.classList.remove('is-locked');
    $$('[data-facets-toggle]').forEach(b => b.setAttribute('aria-expanded', 'false'));
  }));

  /* ---------- Formulaires qui se soumettent seuls -------------------------- */
  /* requestSubmit() ne renvoie rien : l'écrire avec `??` déclencherait une
     seconde soumission. On teste donc la présence de la méthode. */
  $$('[data-auto-submit]').forEach(el => on(el, 'change', () => {
    const form = el.form;
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  }));

  /* ---------- Recommandations différées ------------------------------------ */
  $$('[data-recommendations]').forEach(section => {
    const url = section.dataset.url;
    if (!url) return;
    const load = () => fetch(url)
      .then(r => r.text())
      .then(text => {
        const fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) { section.innerHTML = fresh.innerHTML; bindQty(section); }
        else section.closest('.section')?.remove();
      })
      .catch(() => {});
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (entries.some(e => e.isIntersecting)) { io.disconnect(); load(); }
      }, { rootMargin: '400px' });
      io.observe(section);
    } else load();
  });
})();
